import { useEffect, useState, useRef } from 'react';
import { uploadManager, type UploadEvent } from '../lib/uploadManager';
import { makeT, type Lang } from '../lib/i18n';

export function UploadPanel({ lang, active, onAllDone }: { lang: Lang; active: boolean; onAllDone?: () => void }) {
  const [uploads, setUploads] = useState<UploadEvent[]>([]);
  const t = makeT(lang);
  const onAllDoneRef = useRef(onAllDone);
  onAllDoneRef.current = onAllDone;

  useEffect(() => {
    const unsub = uploadManager.on((e) => {
      setUploads((prev) => {
        const idx = prev.findIndex((p) => p.filename === e.filename && p.id === e.id);
        if (idx === -1) return [...prev, e];
        const next = [...prev];
        next[idx] = e;
        return next;
      });
    });
    return unsub;
  }, []);

  useEffect(() => { uploadManager.recover(); }, []);

  // 全部完成/失败后: 自动隐藏 + 触发刷新
  useEffect(() => {
    if (uploads.length > 0 && uploads.every((u) => u.status === 'done' || u.status === 'failed')) {
      // 刷新列表 + 延迟隐藏面板
      onAllDoneRef.current?.();
      const timer = setTimeout(() => setUploads([]), 1500);
      return () => clearTimeout(timer);
    }
  }, [uploads]);

  if (!active || uploads.length === 0) return null;

  const done = uploads.filter((u) => u.status === 'done').length;
  const msg = (s: UploadEvent['status']) => {
    switch (s) {
      case 'queued': return t('upload.queued');
      case 'uploading': return t('upload.uploading');
      case 'paused': return t('upload.offline');
      case 'failed': return t('upload.failed');
      case 'done': return '✓';
      default: return '';
    }
  };

  return (
    <div className="upload-panel">
      <div className="head">
        <span>{done}/{uploads.length} {t('upload.done')}</span>
      </div>
      {uploads.map((u) => (
        <div className="upload-item" key={u.id}>
          <div className="bar"><div className="fill" style={{ width: (u.progress * 100) + '%' }} /></div>
          <div className="fn">{u.filename}</div>
          <div className="st" style={{ color: u.status === 'failed' ? 'var(--danger)' : undefined }}>
            {u.status === 'failed' ? <span className="retry" onClick={() => uploadManager.retry(u.id)}>{t('upload.retry')}</span> : msg(u.status)}
          </div>
          <div className="pct">{Math.round(u.progress * 100)}%</div>
        </div>
      ))}
    </div>
  );
}
